// ИИ-помощник покупателя (плавающая кнопка «Помощник» на витрине): подбирает
// вещи и магазины по вопросу, учитывает покупки и избранное, тренды берёт из
// открытых источников. Самостоятельный модуль, а не `api.<entity>` общего `Api`:
// у фичи нет мока, и пока шлюз не включит флаг, витрина её просто не видит.
//
// Ручки шлюза (…/api/v1, ответ в конверте `{ success, data }`, как у остальных):
//   GET  /buyer/assistant/status   — без входа, `{ enabled }`; упал/404 → выключено;
//   GET  /buyer/assistant/history  — лента покупателя;
//   POST /buyer/assistant/ask      — `{ message }` → ответ ассистента (10-40 сек);
//   POST /buyer/assistant/clear    — очистить ленту.
import { ApiClientError, createApiClient, type ApiClient } from "@osmix/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useProfile } from "@/store/profile";

import { getBuyerAuthToken } from "./token";

const GATEWAY_URL = process.env.BUN_PUBLIC_API_GATEWAY_URL;

/** Предел длины вопроса — тот же, что проверяет шлюз. */
export const BUYER_ASSISTANT_MAX_LENGTH = 2000;

// Ответ собирается моделью с поиском по каталогу — до 40 сек, берём с запасом.
const ASK_TIMEOUT_MS = 90_000;
const DEFAULT_TIMEOUT_MS = 20_000;

// Массив, который не падает целиком из-за одного кривого элемента: валидные
// отдаём, невалидные пропускаем (как parseItems в http.ts).
const lenientArray = <T extends z.ZodType>(schema: T) =>
	z
		.array(z.unknown())
		.nullish()
		.transform((items) =>
			(items ?? []).flatMap((item) => {
				const parsed = schema.safeParse(item);
				return parsed.success ? [parsed.data as z.infer<T>] : [];
			}),
		);

export const BuyerAssistantProduct = z.object({
	id: z.string(),
	name: z.string(),
	brand: z.string().nullish(),
	price: z.number(),
	oldPrice: z.number().nullish(),
	photo: z.string().nullish(),
	shopName: z.string().nullish(),
	shopSlug: z.string().nullish(),
});
export type BuyerAssistantProduct = z.infer<typeof BuyerAssistantProduct>;

export const BuyerAssistantShop = z.object({
	slug: z.string(),
	name: z.string(),
	logoUrl: z.string().nullish(),
	productCount: z.number().nullish(),
});
export type BuyerAssistantShop = z.infer<typeof BuyerAssistantShop>;

export const BuyerAssistantMessage = z.object({
	id: z.string(),
	role: z.enum(["user", "assistant"]),
	content: z.string(),
	createdAt: z.string(),
	products: lenientArray(BuyerAssistantProduct),
	shops: lenientArray(BuyerAssistantShop),
	usedWeb: z.boolean().nullish(),
});
export type BuyerAssistantMessage = z.infer<typeof BuyerAssistantMessage>;

const History = z.object({ messages: lenientArray(BuyerAssistantMessage) });
const AskResponse = z.object({ message: BuyerAssistantMessage });
const Status = z.object({ enabled: z.boolean() });

// Ошибка помощника: `code` — ключ текста в i18n витрины (`assistant.errors.<code>`),
// панель показывает его через t(), а не готовую строку из этого модуля.
export type BuyerAssistantErrorCode =
	| "unavailable"
	| "timeout"
	| "offline"
	| "session"
	| "rateLimit"
	| "tooLong"
	| "generic";

export class BuyerAssistantError extends Error {
	override readonly name = "BuyerAssistantError";
	constructor(
		readonly code: BuyerAssistantErrorCode,
		readonly status?: number,
	) {
		super(code);
	}
}

type Envelope<T> =
	| { success: true; data: T }
	| { success: false; error: { code: string; message: string; details?: unknown } };

let client: ApiClient | null = null;
const clientFor = (baseUrl: string): ApiClient => {
	client ??= createApiClient({ baseUrl, getBearerToken: getBuyerAuthToken });
	return client;
};

const errorCode = (status: number | undefined, timedOut: boolean): BuyerAssistantErrorCode => {
	if (timedOut) return "timeout";
	if (status === undefined) return "offline";
	if (status === 401 || status === 403) return "session";
	if (status === 429) return "rateLimit";
	if (status === 413 || status === 400) return "tooLong";
	return "generic";
};

const request = async <T>(
	path: string,
	schema: z.ZodType<T>,
	opts: { method?: string; body?: unknown; auth?: boolean; timeoutMs?: number } = {},
): Promise<T> => {
	if (!GATEWAY_URL) throw new BuyerAssistantError("unavailable");
	const signal = AbortSignal.timeout(opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
	let payload: Envelope<unknown> | undefined;
	try {
		payload = await clientFor(GATEWAY_URL).request<Envelope<unknown> | undefined>(path, {
			method: opts.method,
			body: opts.body,
			auth: opts.auth,
			signal,
		});
	} catch (error) {
		if (error instanceof ApiClientError) throw new BuyerAssistantError(errorCode(error.status, false), error.status);
		const timedOut = error instanceof DOMException && (error.name === "TimeoutError" || error.name === "AbortError");
		throw new BuyerAssistantError(errorCode(undefined, timedOut));
	}
	if (!payload || payload.success !== true) throw new BuyerAssistantError("generic", 500);
	const parsed = schema.safeParse(payload.data);
	if (!parsed.success) {
		console.warn(`[buyer-assistant] ${path}: unexpected response`, parsed.error.issues);
		throw new BuyerAssistantError("generic", 500);
	}
	return parsed.data;
};

export const buyerAssistant = {
	// Флаг фичи: любая ошибка (нет ручки на старом шлюзе, сеть) = выключено.
	status: async (): Promise<boolean> => {
		if (!GATEWAY_URL) return false;
		try {
			return (await request("/buyer/assistant/status", Status, { auth: false })).enabled;
		} catch {
			return false;
		}
	},
	history: async () => (await request("/buyer/assistant/history", History)).messages,
	ask: async (message: string) =>
		(
			await request("/buyer/assistant/ask", AskResponse, {
				method: "POST",
				body: { message },
				timeoutMs: ASK_TIMEOUT_MS,
			})
		).message,
	clear: async () =>
		request("/buyer/assistant/clear", z.object({ cleared: z.number() }), { method: "POST", body: {} }),
};

export const buyerAssistantKeys = {
	status: ["buyer-assistant", "status"] as const,
	history: (userId: string) => ["buyer", userId, "assistant", "history"] as const,
};

/** Включён ли помощник на шлюзе. Пока нет — кнопку не показываем вовсе. */
export function useBuyerAssistantEnabled(): boolean {
	const status = useQuery({
		queryKey: buyerAssistantKeys.status,
		queryFn: buyerAssistant.status,
		staleTime: 10 * 60_000,
		retry: false,
		refetchOnWindowFocus: false,
	});
	return status.data === true;
}

const useAssistantUser = () => {
	const token = useProfile((s) => s.token);
	const userId = useProfile((s) => s.user?.id);
	const hydrating = useProfile((s) => s.isHydrating);
	return { userId: userId ?? "guest", signedIn: Boolean(token && userId && !hydrating) };
};

export function useBuyerAssistantHistory(active: boolean) {
	const { userId, signedIn } = useAssistantUser();
	return useQuery({
		queryKey: buyerAssistantKeys.history(userId),
		queryFn: buyerAssistant.history,
		// Ленту грузим, только когда панель открыли: кнопка висит на каждой странице.
		enabled: signedIn && active,
		staleTime: 5 * 60_000,
		retry: 1,
		refetchOnWindowFocus: false,
	});
}

type AskInput = { message: string; /** Повтор после ошибки: реплика уже в ленте. */ retry?: boolean };

// Реплика покупателя появляется в ленте сразу (оптимистично), ответ
// ассистента дописывается, когда придёт. Упал запрос — реплика остаётся,
// «Повторить» отправляет её же, не дублируя в ленте.
export function useAskBuyerAssistant() {
	const { userId } = useAssistantUser();
	const qc = useQueryClient();
	const key = buyerAssistantKeys.history(userId);
	const append = (message: BuyerAssistantMessage) =>
		qc.setQueryData<BuyerAssistantMessage[]>(key, (prev) => [...(prev ?? []), message]);
	return useMutation({
		mutationFn: ({ message }: AskInput) => buyerAssistant.ask(message),
		onMutate: async ({ message, retry }) => {
			await qc.cancelQueries({ queryKey: key });
			if (retry) return;
			append({
				id: `local-${Date.now()}`,
				role: "user",
				content: message,
				createdAt: new Date().toISOString(),
				products: [],
				shops: [],
			});
		},
		onSuccess: (reply) => append(reply),
	});
}

export function useClearBuyerAssistant() {
	const { userId } = useAssistantUser();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: buyerAssistant.clear,
		onSuccess: () => qc.setQueryData<BuyerAssistantMessage[]>(buyerAssistantKeys.history(userId), []),
	});
}
